import { useMemo } from "react";

import type { AutocompleteOption } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";
import type { PrimitiveValueName } from "@skilitics/nautilus-core";
import {
  isNodeValueReference,
  toReferencedValue,
  valueReference,
  type ReferencedValue,
} from "@skilitics/nautilus-core-designer-data";

import { externalValueDescriptors } from "../externalValues.ts";
import { valueIsResolvable } from "../querying/index.ts";
import { useResolveAvailableValues } from "../useResolveAvailableValues.ts";
import { subtypeSatisfies, type ValueSubtypeRequirement } from "../valueSubtypes.ts";

// An option value is an opaque key, never parsed back: the picked option resolves
// to its choice through `choiceByOptionValue`, and the key doubles as the
// canonical form for matching an already-stored config against the list.
const nodeOptionValue = ({ nodeId, name }: { nodeId: number; name: string }) => `node:${nodeId}:${name}`;
const externalOptionValue = (property: string) => `external:${property}`;

/** A pickable value, paired with the config picking it commits. */
export interface ValueBindChoice {
  option: AutocompleteOption;
  config: ReferencedValue;
  type: PrimitiveValueName;
}

export interface ValueBindChoices {
  options: AutocompleteOption[];
  choiceByOptionValue: ReadonlyMap<string, ValueBindChoice>;
  /** The option key matching an already-stored config, or "" when nothing is bound. */
  selectionFor: (config: ReferencedValue | undefined) => string;
}

interface Options {
  /** Restrict the offer to one base type; omit to offer every type. */
  type?: PrimitiveValueName;
  /**
   * Narrow the offer within the base type — only values whose declared subtype
   * satisfies this are offered. Omit to take any value of the base type.
   * Memoise it at the call site: it takes part in this hook's memo deps.
   */
  subtype?: ValueSubtypeRequirement;
  /** Whose scope decides the values on offer (e.g. the trigger's node). */
  scopeNodeId?: number;
  /**
   * A node whose own values to leave out. The caller's call: a node reading its
   * own value is an immediate cycle, so a control that writes back to the scope
   * node passes it — but plenty of pickers legitimately offer the scope node,
   * such as a trigger building an effect that targets it.
   */
  excludeNodeId?: number;
}

/**
 * The values a field can bind to: every resolvable node value in scope plus the
 * environment's external values, as one list — the author doesn't distinguish
 * them. The single source for every value picker, so a node never assembles its
 * own target list.
 */
export const useValueBindChoices = ({ type, subtype, scopeNodeId, excludeNodeId }: Options): ValueBindChoices => {
  const formatMessage = useFormatMessage(formatStrings);
  const resolveAvailableValues = useResolveAvailableValues();

  return useMemo(() => {
    const nodeValues =
      scopeNodeId === undefined
        ? []
        : resolveAvailableValues(scopeNodeId)
            .filter(
              (available) =>
                available.nodeId !== excludeNodeId &&
                (!type || available.type === type) &&
                subtypeSatisfies(subtype, available.subtype) &&
                valueIsResolvable(available),
            )
            .map(
              (available): ValueBindChoice => ({
                option: {
                  label: available.title,
                  value: nodeOptionValue(available),
                  group: available.category,
                },
                config: toReferencedValue({ nodeId: available.nodeId, name: available.name }),
                type: available.type,
              }),
            );

    const externals = externalValueDescriptors
      .filter(
        (descriptor) =>
          descriptor.offered !== false &&
          (!type || descriptor.type === type) &&
          subtypeSatisfies(subtype, descriptor.subtype),
      )
      .map(
        (descriptor): ValueBindChoice => ({
          option: {
            label: descriptor.title,
            value: externalOptionValue(descriptor.property),
            group: formatMessage("environmentGroup"),
          },
          config: toReferencedValue({ property: descriptor.property }),
          type: descriptor.type,
        }),
      );

    // Grouped presentation: ungrouped choices lead (no header), then groups alphabetically.
    const unordered = [...nodeValues, ...externals];
    const groupOf = (choice: ValueBindChoice) => choice.option.group ?? "";
    const groups = [...new Set(unordered.map(groupOf))].sort((a, b) =>
      a === "" ? -1 : b === "" ? 1 : a.localeCompare(b),
    );
    const choices = groups.flatMap((group) => unordered.filter((choice) => groupOf(choice) === group));

    return {
      options: choices.map((choice) => choice.option),
      choiceByOptionValue: new Map(choices.map((choice) => [choice.option.value, choice])),
      selectionFor: (config: ReferencedValue | undefined) => {
        if (!config) return "";
        const reference = valueReference(config);
        return isNodeValueReference(reference) ? nodeOptionValue(reference) : externalOptionValue(reference.property);
      },
    };
  }, [excludeNodeId, formatMessage, resolveAvailableValues, scopeNodeId, subtype, type]);
};

const formatStrings = {
  environmentGroup: {
    en: "Environment",
  },
};
